// Flyer Theme Definitions and Renderers

// ============ FORWARD TYPE DECLARATIONS ============
// These types are used in interfaces below and defined in detail later

export type CardSize = 'compact' | 'normal' | 'large';
export type CardDesign = 'simple' | 'badge' | 'photo';
export type CardStyle = 'flat' | 'raised' | 'outlined' | 'minimal';
export type BadgeType = 'new' | 'hot' | 'limited' | 'sale' | 'best';
export type BgPattern = 'solid' | 'gradient' | 'striped' | 'dots';
export type DividerStyle = 'line' | 'dashed' | 'dotted' | 'wave';

export interface PriceFormat {
  showOriginalPrice?: boolean;
  showSavings?: boolean;
  showUnitPrice?: boolean;
}

// ============ TYPE DEFINITIONS ============

export interface SectionConfig {
  id: string;
  title: string;
  chineseTitle: string;
  headerColor: string;
  bgColor: string;
  rows: number;
  cols: number;
  order: number;
  visible: boolean;
  categoryFilter?: string; // Optional: auto-filter products by category
  cardSize?: CardSize; // Optional: card size for this section
  cardDesign?: CardDesign; // Optional: card design style for this section
  cardStyle?: CardStyle; // Optional: card border/shadow style
  pageBreakAfter?: boolean; // Optional: force page break after this section (for multi-page flyers)
  priceFormat?: PriceFormat; // Optional: price display options
  bgPattern?: BgPattern; // Optional: background pattern
  showDivider?: boolean; // Optional: show divider below section
  dividerStyle?: DividerStyle; // Optional: divider style
}

export interface ColorScheme {
  primary: string;
  secondary: string;
  accent: string;
  headerBg: string;
  footerBg: string;
}

export interface FlyerTheme {
  id: string;
  name: string;
  description: string;
  headerStyle: 'classic' | 'festive' | 'minimal';
  footerStyle: 'full' | 'compact' | 'minimal';
  defaultSections: SectionConfig[];
  colorScheme: ColorScheme;
  thumbnail: string; // Emoji or icon identifier
}

export interface StoreConfig {
  storeName: string;
  storeNameChinese: string;
  tagline: string;
  taglineChinese: string;
  address: string;
  hours: string;
  phone: string;
  website: string;
  logoUrl: string;
  qrCodeUrl?: string;
  showQrCode?: boolean;
  // Banner image options
  bannerImageUrl?: string;
  bannerPosition?: 'header' | 'footer' | 'both';
  bannerOpacity?: number; // 0-100
}

// ============ HEADER TITLE CONFIG ============

export interface TextStyle {
  fontSize: 'sm' | 'md' | 'lg' | 'xl';
  color: string;
  align: 'left' | 'center' | 'right';
  bold?: boolean;
  italic?: boolean;
  // Enhanced text options
  textShadow?: boolean;
  letterSpacing?: 'normal' | 'wide' | 'wider';
  textTransform?: 'none' | 'uppercase' | 'capitalize';
}

export interface HeaderTitleConfig {
  title: string;
  titleChinese?: string;
  titleStyle: TextStyle;
  subtitle?: string;
  subtitleChinese?: string;
  subtitleStyle: TextStyle;
}

export interface TitlePreset {
  id: string;
  name: string;
  title: string;
  titleChinese?: string;
  subtitle?: string;
  subtitleChinese?: string;
}

// ============ FLYER TEMPLATE ============

export interface FlyerTemplate {
  id: string;
  name: string;
  createdAt: string;
  themeId: string;
  sections: SectionConfig[];
  headerTitleConfig: HeaderTitleConfig;
  storeConfig: StoreConfig;
  thumbnail?: string; // Base64 encoded preview image
  tags?: string[];    // 'holiday', 'weekly', 'clearance', etc.
}

// ============ DATE PRESETS ============

export interface DatePreset {
  id: string;
  label: string;
  getRange: () => { start: string; end: string };
}

export const DATE_PRESETS: DatePreset[] = [
  {
    id: 'this-week',
    label: 'This Week',
    getRange: () => {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay()); // Sunday
      const end = new Date(start);
      end.setDate(start.getDate() + 6); // Saturday
      return {
        start: start.toISOString().split('T')[0],
        end: end.toISOString().split('T')[0],
      };
    },
  },
  {
    id: 'next-week',
    label: 'Next Week',
    getRange: () => {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - now.getDay() + 7); // Next Sunday
      const end = new Date(start);
      end.setDate(start.getDate() + 6); // Next Saturday
      return {
        start: start.toISOString().split('T')[0],
        end: end.toISOString().split('T')[0],
      };
    },
  },
  {
    id: 'this-weekend',
    label: 'This Weekend',
    getRange: () => {
      const now = new Date();
      const friday = new Date(now);
      friday.setDate(now.getDate() + (5 - now.getDay() + 7) % 7);
      const sunday = new Date(friday);
      sunday.setDate(friday.getDate() + 2);
      return {
        start: friday.toISOString().split('T')[0],
        end: sunday.toISOString().split('T')[0],
      };
    },
  },
  {
    id: 'next-7-days',
    label: 'Next 7 Days',
    getRange: () => {
      const now = new Date();
      const end = new Date(now);
      end.setDate(now.getDate() + 6);
      return {
        start: now.toISOString().split('T')[0],
        end: end.toISOString().split('T')[0],
      };
    },
  },
];

// ============ TEMPLATE STORAGE ============

const TEMPLATES_STORAGE_KEY = 'flyer-templates';

export function saveTemplate(template: FlyerTemplate): void {
  const templates = getTemplates();
  const existingIndex = templates.findIndex(t => t.id === template.id);
  if (existingIndex >= 0) {
    templates[existingIndex] = template;
  } else {
    templates.push(template);
  }
  localStorage.setItem(TEMPLATES_STORAGE_KEY, JSON.stringify(templates));
}

export function getTemplates(): FlyerTemplate[] {
  try {
    const stored = localStorage.getItem(TEMPLATES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function deleteTemplate(id: string): void {
  const templates = getTemplates().filter(t => t.id !== id);
  localStorage.setItem(TEMPLATES_STORAGE_KEY, JSON.stringify(templates));
}

export function generateTemplateId(): string {
  return `template-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// ============ GALLERY TEMPLATES (Pre-built) ============

export interface GalleryTemplate {
  id: string;
  name: string;
  description: string;
  thumbnail: string; // Emoji or icon
  category: 'weekly' | 'holiday' | 'clearance' | 'special';
  themeId: string;
  sections: SectionConfig[];
  headerTitleConfig: Partial<HeaderTitleConfig>;
  tags: string[];
}

export const GALLERY_TEMPLATES: GalleryTemplate[] = [
  {
    id: 'weekly-classic',
    name: 'Weekly Specials',
    description: 'Traditional weekly sale flyer with produce, meat, and grocery sections',
    thumbnail: '📰',
    category: 'weekly',
    themeId: 'classic',
    sections: [
      { id: 'produce', title: 'FRESH PRODUCE', chineseTitle: '新鮮蔬果', headerColor: 'bg-green-600', bgColor: 'bg-green-50', rows: 2, cols: 7, order: 0, visible: true },
      { id: 'meat', title: 'MEAT & POULTRY', chineseTitle: '肉類家禽', headerColor: 'bg-red-600', bgColor: 'bg-red-50', rows: 2, cols: 7, order: 1, visible: true },
      { id: 'grocery', title: 'GROCERY DEALS', chineseTitle: '雜貨優惠', headerColor: 'bg-amber-500', bgColor: 'bg-amber-50', rows: 3, cols: 7, order: 2, visible: true },
    ],
    headerTitleConfig: { mainTitle: 'WEEKLY SPECIALS', chineseTitle: '每週特價' },
    tags: ['weekly', 'standard'],
  },
  {
    id: 'mega-sale',
    name: 'Mega Sale',
    description: 'High-impact sale flyer with featured deals and clearance',
    thumbnail: '🔥',
    category: 'special',
    themeId: 'festive',
    sections: [
      { id: 'featured', title: 'MEGA DEALS', chineseTitle: '超級優惠', headerColor: 'bg-yellow-500', bgColor: 'bg-yellow-50', rows: 2, cols: 5, order: 0, visible: true },
      { id: 'produce', title: 'FRESH PICKS', chineseTitle: '精選新鮮', headerColor: 'bg-green-600', bgColor: 'bg-green-50', rows: 2, cols: 6, order: 1, visible: true },
      { id: 'meat', title: 'MEAT SPECIALS', chineseTitle: '肉類特價', headerColor: 'bg-red-600', bgColor: 'bg-red-50', rows: 2, cols: 6, order: 2, visible: true },
      { id: 'clearance', title: 'CLEARANCE', chineseTitle: '清倉大減價', headerColor: 'bg-orange-600', bgColor: 'bg-orange-50', rows: 2, cols: 6, order: 3, visible: true },
    ],
    headerTitleConfig: { mainTitle: 'MEGA SALE', chineseTitle: '超級特賣' },
    tags: ['sale', 'promotion'],
  },
  {
    id: 'chinese-new-year',
    name: 'Chinese New Year',
    description: 'Festive template for Lunar New Year celebrations',
    thumbnail: '🧧',
    category: 'holiday',
    themeId: 'festive',
    sections: [
      { id: 'lucky', title: 'LUCKY DEALS', chineseTitle: '新春好運價', headerColor: 'bg-red-700', bgColor: 'bg-red-50', rows: 2, cols: 6, order: 0, visible: true },
      { id: 'seafood', title: 'SEAFOOD FEAST', chineseTitle: '海鮮盛宴', headerColor: 'bg-blue-600', bgColor: 'bg-blue-50', rows: 2, cols: 6, order: 1, visible: true },
      { id: 'produce', title: 'FRESH PRODUCE', chineseTitle: '新鮮蔬果', headerColor: 'bg-green-600', bgColor: 'bg-green-50', rows: 2, cols: 6, order: 2, visible: true },
      { id: 'snacks', title: 'FESTIVE SNACKS', chineseTitle: '節慶零食', headerColor: 'bg-amber-500', bgColor: 'bg-amber-50', rows: 2, cols: 6, order: 3, visible: true },
    ],
    headerTitleConfig: { mainTitle: 'CHINESE NEW YEAR SALE', chineseTitle: '新春大特賣' },
    tags: ['holiday', 'chinese', 'lunar'],
  },
  {
    id: 'summer-bbq',
    name: 'Summer BBQ',
    description: 'Grilling season with meats, produce, and outdoor essentials',
    thumbnail: '🍖',
    category: 'special',
    themeId: 'classic',
    sections: [
      { id: 'grill', title: 'GRILL FAVORITES', chineseTitle: '燒烤首選', headerColor: 'bg-orange-600', bgColor: 'bg-orange-50', rows: 2, cols: 6, order: 0, visible: true },
      { id: 'meat', title: 'MEATS & SAUSAGES', chineseTitle: '肉類香腸', headerColor: 'bg-red-600', bgColor: 'bg-red-50', rows: 2, cols: 6, order: 1, visible: true },
      { id: 'produce', title: 'FRESH SIDES', chineseTitle: '新鮮配菜', headerColor: 'bg-green-600', bgColor: 'bg-green-50', rows: 2, cols: 6, order: 2, visible: true },
      { id: 'beverages', title: 'COLD DRINKS', chineseTitle: '冰涼飲料', headerColor: 'bg-blue-500', bgColor: 'bg-blue-50', rows: 2, cols: 6, order: 3, visible: true },
    ],
    headerTitleConfig: { mainTitle: 'SUMMER BBQ SALE', chineseTitle: '夏日燒烤特賣' },
    tags: ['summer', 'bbq', 'outdoor'],
  },
  {
    id: 'clearance-blowout',
    name: 'Clearance Blowout',
    description: 'Maximum density layout for clearance items',
    thumbnail: '💥',
    category: 'clearance',
    themeId: 'grid',
    sections: [
      { id: 'clearance1', title: 'UP TO 50% OFF', chineseTitle: '五折起', headerColor: 'bg-red-600', bgColor: 'bg-red-50', rows: 3, cols: 8, order: 0, visible: true },
      { id: 'clearance2', title: 'LAST CHANCE', chineseTitle: '最後機會', headerColor: 'bg-orange-600', bgColor: 'bg-orange-50', rows: 3, cols: 8, order: 1, visible: true },
      { id: 'clearance3', title: 'MUST GO', chineseTitle: '必須清', headerColor: 'bg-yellow-500', bgColor: 'bg-yellow-50', rows: 3, cols: 8, order: 2, visible: true },
    ],
    headerTitleConfig: { mainTitle: 'CLEARANCE BLOWOUT', chineseTitle: '清倉大特賣' },
    tags: ['clearance', 'discount'],
  },
  {
    id: 'restaurant-supply',
    name: 'Restaurant Supply',
    description: 'Bulk pricing for restaurant and business customers',
    thumbnail: '🍳',
    category: 'special',
    themeId: 'grid',
    sections: [
      { id: 'bulk-meat', title: 'BULK MEATS', chineseTitle: '大量肉類', headerColor: 'bg-red-700', bgColor: 'bg-red-50', rows: 2, cols: 6, order: 0, visible: true },
      { id: 'bulk-produce', title: 'BULK PRODUCE', chineseTitle: '大量蔬果', headerColor: 'bg-green-700', bgColor: 'bg-green-50', rows: 2, cols: 6, order: 1, visible: true },
      { id: 'bulk-grocery', title: 'BULK GROCERY', chineseTitle: '大量雜貨', headerColor: 'bg-amber-700', bgColor: 'bg-amber-50', rows: 2, cols: 6, order: 2, visible: true },
      { id: 'supplies', title: 'SUPPLIES', chineseTitle: '餐廳用品', headerColor: 'bg-gray-600', bgColor: 'bg-gray-50', rows: 2, cols: 6, order: 3, visible: true },
    ],
    headerTitleConfig: { mainTitle: 'RESTAURANT SPECIALS', chineseTitle: '餐廳特價' },
    tags: ['restaurant', 'bulk', 'wholesale'],
  },
  {
    id: 'fresh-market',
    name: 'Fresh Market',
    description: 'Focus on fresh produce, seafood, and dairy',
    thumbnail: '🥬',
    category: 'weekly',
    themeId: 'classic',
    sections: [
      { id: 'produce', title: 'FARM FRESH', chineseTitle: '農場新鮮', headerColor: 'bg-green-600', bgColor: 'bg-green-50', rows: 3, cols: 7, order: 0, visible: true },
      { id: 'seafood', title: 'FRESH SEAFOOD', chineseTitle: '新鮮海產', headerColor: 'bg-blue-600', bgColor: 'bg-blue-50', rows: 2, cols: 7, order: 1, visible: true },
      { id: 'dairy', title: 'DAIRY & EGGS', chineseTitle: '乳製品雞蛋', headerColor: 'bg-yellow-500', bgColor: 'bg-yellow-50', rows: 2, cols: 7, order: 2, visible: true },
    ],
    headerTitleConfig: { mainTitle: 'FRESH MARKET', chineseTitle: '新鮮市場' },
    tags: ['fresh', 'produce', 'healthy'],
  },
  {
    id: 'thanksgiving',
    name: 'Thanksgiving',
    description: 'Turkey, sides, and holiday feast essentials',
    thumbnail: '🦃',
    category: 'holiday',
    themeId: 'festive',
    sections: [
      { id: 'turkey', title: 'TURKEY & HAM', chineseTitle: '火雞火腿', headerColor: 'bg-amber-700', bgColor: 'bg-amber-50', rows: 2, cols: 5, order: 0, visible: true },
      { id: 'sides', title: 'HOLIDAY SIDES', chineseTitle: '節日配菜', headerColor: 'bg-orange-600', bgColor: 'bg-orange-50', rows: 2, cols: 6, order: 1, visible: true },
      { id: 'produce', title: 'FRESH PRODUCE', chineseTitle: '新鮮蔬果', headerColor: 'bg-green-600', bgColor: 'bg-green-50', rows: 2, cols: 6, order: 2, visible: true },
      { id: 'dessert', title: 'DESSERTS', chineseTitle: '甜點', headerColor: 'bg-pink-500', bgColor: 'bg-pink-50', rows: 2, cols: 6, order: 3, visible: true },
    ],
    headerTitleConfig: { mainTitle: 'THANKSGIVING FEAST', chineseTitle: '感恩節盛宴' },
    tags: ['holiday', 'thanksgiving', 'feast'],
  },
];

// ============ CARD SIZE PRESETS ============

export interface CardSizeConfig {
  id: CardSize;
  label: string;
  imageHeight: string;
  titleSize: string;
  priceSize: string;
  padding: string;
  description: string;
}

export const CARD_SIZE_PRESETS: CardSizeConfig[] = [
  {
    id: 'compact',
    label: 'Compact',
    imageHeight: 'h-10',
    titleSize: 'text-[7px]',
    priceSize: 'text-xs',
    padding: 'p-0.5',
    description: 'Maximum density - fits most products',
  },
  {
    id: 'normal',
    label: 'Normal',
    imageHeight: 'h-14',
    titleSize: 'text-[9px]',
    priceSize: 'text-sm',
    padding: 'p-1',
    description: 'Balanced size and readability',
  },
  {
    id: 'large',
    label: 'Large',
    imageHeight: 'h-20',
    titleSize: 'text-[11px]',
    priceSize: 'text-base',
    padding: 'p-1.5',
    description: 'Easier to read - fewer products',
  },
];

export function getCardSizeConfig(size: CardSize): CardSizeConfig {
  return CARD_SIZE_PRESETS.find(p => p.id === size) || CARD_SIZE_PRESETS[1];
}

// ============ PRODUCT BADGES ============

export interface ProductBadge {
  id: BadgeType;
  label: string;
  color: string;
}

export const PRODUCT_BADGES: ProductBadge[] = [
  { id: 'new', label: 'NEW', color: 'bg-blue-500' },
  { id: 'hot', label: 'HOT DEAL', color: 'bg-red-500' },
  { id: 'limited', label: 'LIMITED', color: 'bg-orange-500' },
  { id: 'sale', label: 'SALE', color: 'bg-green-500' },
  { id: 'best', label: 'BEST VALUE', color: 'bg-purple-500' },
];

export function getBadgeConfig(badge: BadgeType): ProductBadge | undefined {
  return PRODUCT_BADGES.find(b => b.id === badge);
}

// ============ CARD DESIGN STYLES ============

export interface CardDesignConfig {
  id: CardDesign;
  label: string;
  description: string;
  features: string[];
}

export const CARD_DESIGN_PRESETS: CardDesignConfig[] = [
  {
    id: 'simple',
    label: 'Simple',
    description: 'Clean, minimal layout',
    features: ['Focus on price', 'Compact text', 'Standard image'],
  },
  {
    id: 'badge',
    label: 'Badge-Focused',
    description: 'Prominent badges & deals',
    features: ['Large badges', 'Bold pricing', 'Deal emphasis'],
  },
  {
    id: 'photo',
    label: 'Photo-Dominant',
    description: 'Large product images',
    features: ['Bigger images', 'Overlay text', 'Visual appeal'],
  },
];

export function getCardDesignConfig(design: CardDesign): CardDesignConfig {
  return CARD_DESIGN_PRESETS.find(p => p.id === design) || CARD_DESIGN_PRESETS[0];
}

// ============ CARD STYLE PRESETS ============

export interface CardStyleConfig {
  id: CardStyle;
  label: string;
  description: string;
  className: string;
}

export const CARD_STYLE_PRESETS: CardStyleConfig[] = [
  {
    id: 'flat',
    label: 'Flat',
    description: 'Clean, no shadow',
    className: 'border border-gray-200 rounded',
  },
  {
    id: 'raised',
    label: 'Raised',
    description: 'Elevated with shadow',
    className: 'shadow-md rounded-lg border-0',
  },
  {
    id: 'outlined',
    label: 'Outlined',
    description: 'Bold border',
    className: 'border-2 border-gray-400 rounded',
  },
  {
    id: 'minimal',
    label: 'Minimal',
    description: 'No border or shadow',
    className: 'border-0 shadow-none',
  },
];

export function getCardStyleConfig(style: CardStyle): CardStyleConfig {
  return CARD_STYLE_PRESETS.find(p => p.id === style) || CARD_STYLE_PRESETS[0];
}

// ============ BACKGROUND PATTERNS ============

export interface BgPatternConfig {
  id: BgPattern;
  label: string;
  getStyle: (baseColor: string) => string;
}

export const BG_PATTERN_PRESETS: BgPatternConfig[] = [
  {
    id: 'solid',
    label: 'Solid',
    getStyle: (baseColor) => baseColor,
  },
  {
    id: 'gradient',
    label: 'Gradient',
    getStyle: (baseColor) => `bg-gradient-to-br ${baseColor} to-white/30`,
  },
  {
    id: 'striped',
    label: 'Striped',
    getStyle: (baseColor) => `${baseColor} bg-stripes`,
  },
  {
    id: 'dots',
    label: 'Dots',
    getStyle: (baseColor) => `${baseColor} bg-dots`,
  },
];

// ============ DIVIDER STYLES ============

export interface DividerStyleConfig {
  id: DividerStyle;
  label: string;
  className: string;
}

export const DIVIDER_STYLE_PRESETS: DividerStyleConfig[] = [
  { id: 'line', label: 'Solid Line', className: 'border-b-2 border-gray-300' },
  { id: 'dashed', label: 'Dashed', className: 'border-b-2 border-dashed border-gray-300' },
  { id: 'dotted', label: 'Dotted', className: 'border-b-2 border-dotted border-gray-400' },
  { id: 'wave', label: 'Wave', className: 'bg-wave-pattern h-2' },
];

// ============ SAVED FLYERS (WITH PRODUCTS) ============

export interface SavedFlyerProduct {
  productId: number;
  sectionId: string;
  displayOrder: number;
  flyerPrice?: number;
  badge?: BadgeType;
}

export interface SavedFlyer {
  id: string;
  name: string;
  createdAt: string;
  themeId: string;
  sections: SectionConfig[];
  headerTitleConfig: HeaderTitleConfig;
  storeConfig: StoreConfig;
  globalCardDesign: CardDesign;
  validDates: { start: string; end: string };
  products: SavedFlyerProduct[];
}

const FLYERS_STORAGE_KEY = 'saved-flyers';

export function saveFlyer(flyer: SavedFlyer): void {
  const flyers = getSavedFlyers();
  const existingIndex = flyers.findIndex(f => f.id === flyer.id);
  if (existingIndex >= 0) {
    flyers[existingIndex] = flyer;
  } else {
    flyers.push(flyer);
  }
  localStorage.setItem(FLYERS_STORAGE_KEY, JSON.stringify(flyers));
}

export function getSavedFlyers(): SavedFlyer[] {
  try {
    const stored = localStorage.getItem(FLYERS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function deleteSavedFlyer(id: string): void {
  const flyers = getSavedFlyers().filter(f => f.id !== id);
  localStorage.setItem(FLYERS_STORAGE_KEY, JSON.stringify(flyers));
}

export function generateFlyerId(): string {
  return `flyer-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// ============ FLYER EXPORT/IMPORT ============

export function exportFlyerAsJSON(flyer: SavedFlyer): void {
  const blob = new Blob([JSON.stringify(flyer, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${flyer.name.replace(/[^a-z0-9]/gi, '-')}-${new Date().toISOString().split('T')[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function importFlyerFromJSON(file: File): Promise<SavedFlyer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        // Validate structure
        if (!data.name || !data.sections || !data.products) {
          throw new Error('Invalid flyer format: missing required fields');
        }
        // Generate new ID to avoid conflicts
        data.id = generateFlyerId();
        data.createdAt = new Date().toISOString();
        resolve(data as SavedFlyer);
      } catch (err) {
        reject(new Error('Failed to parse flyer file: ' + (err as Error).message));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

// ============ BACKUP/RESTORE ============

export interface FlyerBackup {
  version: number;
  exportedAt: string;
  flyers: SavedFlyer[];
  templates: FlyerTemplate[];
  settings: AppSettings;
}

export function backupAllData(): void {
  const backup: FlyerBackup = {
    version: 1,
    exportedAt: new Date().toISOString(),
    flyers: getSavedFlyers(),
    templates: getTemplates(),
    settings: getAppSettings(),
  };

  const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `flyer-backup-${new Date().toISOString().split('T')[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function restoreFromBackup(file: File): Promise<{ flyers: number; templates: number }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string) as FlyerBackup;

        // Validate backup structure
        if (!data.version || !data.flyers || !data.templates) {
          throw new Error('Invalid backup format');
        }

        // Restore flyers (merge with existing)
        const existingFlyers = getSavedFlyers();
        const existingIds = new Set(existingFlyers.map(f => f.id));
        let flyersAdded = 0;
        data.flyers.forEach(flyer => {
          if (!existingIds.has(flyer.id)) {
            existingFlyers.push(flyer);
            flyersAdded++;
          }
        });
        localStorage.setItem(FLYERS_STORAGE_KEY, JSON.stringify(existingFlyers));

        // Restore templates (merge with existing)
        const existingTemplates = getTemplates();
        const existingTemplateIds = new Set(existingTemplates.map(t => t.id));
        let templatesAdded = 0;
        data.templates.forEach(template => {
          if (!existingTemplateIds.has(template.id)) {
            existingTemplates.push(template);
            templatesAdded++;
          }
        });
        localStorage.setItem(TEMPLATES_STORAGE_KEY, JSON.stringify(existingTemplates));

        // Restore settings (overwrite)
        if (data.settings) {
          saveAppSettings(data.settings);
        }

        resolve({ flyers: flyersAdded, templates: templatesAdded });
      } catch (err) {
        reject(new Error('Failed to restore backup: ' + (err as Error).message));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read backup file'));
    reader.readAsText(file);
  });
}

// ============ APP SETTINGS ============

export interface AppSettings {
  defaultCardDesign: CardDesign;
  defaultCardSize: CardSize;
  defaultThemeId: string;
}

const APP_SETTINGS_KEY = 'flyer-app-settings';

export const DEFAULT_APP_SETTINGS: AppSettings = {
  defaultCardDesign: 'simple',
  defaultCardSize: 'normal',
  defaultThemeId: 'classic',
};

export function getAppSettings(): AppSettings {
  try {
    const stored = localStorage.getItem(APP_SETTINGS_KEY);
    if (stored) {
      return { ...DEFAULT_APP_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (e) {
    console.error('Failed to load app settings:', e);
  }
  return DEFAULT_APP_SETTINGS;
}

export function saveAppSettings(settings: Partial<AppSettings>): void {
  try {
    const current = getAppSettings();
    localStorage.setItem(APP_SETTINGS_KEY, JSON.stringify({ ...current, ...settings }));
  } catch (e) {
    console.error('Failed to save app settings:', e);
  }
}

// ============ TITLE PRESETS ============

export const TITLE_PRESETS: TitlePreset[] = [
  { id: 'weekly', name: 'Weekly Specials', title: 'WEEKLY SPECIALS', titleChinese: '每週特價', subtitle: 'Fresh Deals Every Week!', subtitleChinese: '每週新鮮優惠！' },
  { id: 'grand-opening', name: 'Grand Opening', title: 'GRAND OPENING', titleChinese: '隆重開幕', subtitle: 'Celebrate With Us!', subtitleChinese: '與我們一同慶祝！' },
  { id: 'holiday', name: 'Holiday Sale', title: 'HOLIDAY SALE', titleChinese: '節日特賣', subtitle: 'Season\'s Best Deals', subtitleChinese: '節日最佳優惠' },
  { id: 'anniversary', name: 'Anniversary Sale', title: 'ANNIVERSARY SALE', titleChinese: '週年慶', subtitle: 'Thank You For Your Support!', subtitleChinese: '感謝您的支持！' },
  { id: 'clearance', name: 'Clearance Event', title: 'CLEARANCE EVENT', titleChinese: '清倉大促', subtitle: 'Everything Must Go!', subtitleChinese: '全場清倉！' },
  { id: 'cny', name: 'Chinese New Year', title: 'CHINESE NEW YEAR', titleChinese: '農曆新年', subtitle: 'Celebrate the Year of the Snake!', subtitleChinese: '恭賀新禧！' },
  { id: 'summer', name: 'Summer Sale', title: 'SUMMER SALE', titleChinese: '夏日特賣', subtitle: 'Hot Deals for Hot Days!', subtitleChinese: '夏日熱賣！' },
  { id: 'back-to-school', name: 'Back to School', title: 'BACK TO SCHOOL', titleChinese: '開學季', subtitle: 'Stock Up & Save!', subtitleChinese: '備貨省錢！' },
  { id: 'custom', name: 'Custom', title: '', titleChinese: '', subtitle: '', subtitleChinese: '' },
];

export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontSize: 'lg',
  color: 'text-white',
  align: 'center',
  bold: true,
};

export const DEFAULT_HEADER_TITLE_CONFIG: HeaderTitleConfig = {
  title: 'WEEKLY SPECIALS',
  titleChinese: '每週特價',
  titleStyle: { fontSize: 'lg', color: 'text-white', align: 'right', bold: true },
  subtitle: '',
  subtitleChinese: '',
  subtitleStyle: { fontSize: 'sm', color: 'text-yellow-300', align: 'right', bold: false },
};

// ============ TEXT STYLE OPTIONS ============

export const FONT_SIZE_OPTIONS = [
  { value: 'sm', label: 'Small', class: 'text-sm' },
  { value: 'md', label: 'Medium', class: 'text-base' },
  { value: 'lg', label: 'Large', class: 'text-lg' },
  { value: 'xl', label: 'Extra Large', class: 'text-xl' },
];

export const TEXT_COLOR_OPTIONS = [
  { value: 'text-white', label: 'White', preview: 'bg-white' },
  { value: 'text-yellow-300', label: 'Yellow', preview: 'bg-yellow-300' },
  { value: 'text-yellow-400', label: 'Gold', preview: 'bg-yellow-400' },
  { value: 'text-red-600', label: 'Red', preview: 'bg-red-600' },
  { value: 'text-red-800', label: 'Dark Red', preview: 'bg-red-800' },
  { value: 'text-gray-800', label: 'Dark Gray', preview: 'bg-gray-800' },
  { value: 'text-emerald-400', label: 'Emerald', preview: 'bg-emerald-400' },
];

export function getTextStyleClasses(style: TextStyle): string {
  const sizeClass = FONT_SIZE_OPTIONS.find(o => o.value === style.fontSize)?.class || 'text-base';
  const alignClass = style.align === 'left' ? 'text-left' : style.align === 'right' ? 'text-right' : 'text-center';
  const boldClass = style.bold ? 'font-bold' : '';
  const italicClass = style.italic ? 'italic' : '';
  const shadowClass = style.textShadow ? 'drop-shadow-md' : '';
  const spacingClass = style.letterSpacing === 'wide' ? 'tracking-wide' : style.letterSpacing === 'wider' ? 'tracking-wider' : '';
  const transformClass = style.textTransform === 'uppercase' ? 'uppercase' : style.textTransform === 'capitalize' ? 'capitalize' : '';
  return `${sizeClass} ${style.color} ${alignClass} ${boldClass} ${italicClass} ${shadowClass} ${spacingClass} ${transformClass}`.trim().replace(/\s+/g, ' ');
}

// ============ DEFAULT STORE CONFIG ============

export const DEFAULT_STORE_CONFIG: StoreConfig = {
  storeName: 'TOPS SUPERMARKET',
  storeNameChinese: '頂好超市',
  tagline: 'Best Choice for Fresh Products',
  taglineChinese: '新鮮優質之選',
  address: '1623 Centre St NW, Calgary, AB',
  hours: 'Open Daily 9AM - 6PM',
  phone: '(403) 555-0123',
  website: 'www.topssupermarket.ca',
  logoUrl: '/images/TopsLogoCrop.png',
  qrCodeUrl: 'https://www.topssupermarket.ca',
  showQrCode: false,
};

// ============ THEME DEFINITIONS ============

export const THEMES: FlyerTheme[] = [
  {
    id: 'classic',
    name: 'Classic',
    description: 'Traditional dense flyer layout',
    thumbnail: '📰',
    headerStyle: 'classic',
    footerStyle: 'full',
    colorScheme: {
      primary: 'bg-red-700',
      secondary: 'bg-red-600',
      accent: 'bg-yellow-400',
      headerBg: 'bg-gradient-to-r from-red-700 via-red-600 to-red-700',
      footerBg: 'bg-red-700',
    },
    defaultSections: [
      { id: 'produce', title: 'PRODUCE', chineseTitle: '新鮮蔬果', headerColor: 'bg-green-600', bgColor: 'bg-green-50', rows: 2, cols: 7, order: 0, visible: true },
      { id: 'restaurant', title: 'RESTAURANT SPECIAL', chineseTitle: '餐廳特價', headerColor: 'bg-red-600', bgColor: 'bg-red-50', rows: 2, cols: 9, order: 1, visible: true },
      { id: 'grocery', title: 'GROCERY', chineseTitle: '雜貨', headerColor: 'bg-amber-500', bgColor: 'bg-amber-50', rows: 3, cols: 5, order: 2, visible: true },
      { id: 'halal', title: 'HALAL', chineseTitle: '清真', headerColor: 'bg-blue-600', bgColor: 'bg-blue-50', rows: 3, cols: 5, order: 3, visible: true },
    ],
  },
  {
    id: 'festive',
    name: 'Festive',
    description: 'Holiday & celebration style',
    thumbnail: '🎊',
    headerStyle: 'festive',
    footerStyle: 'full',
    colorScheme: {
      primary: 'bg-red-800',
      secondary: 'bg-yellow-500',
      accent: 'bg-yellow-400',
      headerBg: 'bg-gradient-to-r from-red-800 via-red-700 to-red-800',
      footerBg: 'bg-red-800',
    },
    defaultSections: [
      { id: 'featured', title: 'STAR DEALS', chineseTitle: '明星優惠', headerColor: 'bg-yellow-500', bgColor: 'bg-yellow-50', rows: 2, cols: 5, order: 0, visible: true },
      { id: 'produce', title: 'FRESH PRODUCE', chineseTitle: '新鮮蔬果', headerColor: 'bg-green-600', bgColor: 'bg-green-50', rows: 2, cols: 6, order: 1, visible: true },
      { id: 'meat', title: 'MEAT & SEAFOOD', chineseTitle: '肉類海鮮', headerColor: 'bg-red-600', bgColor: 'bg-red-50', rows: 2, cols: 6, order: 2, visible: true },
      { id: 'grocery', title: 'GROCERY DEALS', chineseTitle: '雜貨優惠', headerColor: 'bg-amber-600', bgColor: 'bg-amber-50', rows: 3, cols: 6, order: 3, visible: true },
    ],
  },
  {
    id: 'grid',
    name: 'Grid',
    description: 'Maximum density layout',
    thumbnail: '🔲',
    headerStyle: 'minimal',
    footerStyle: 'compact',
    colorScheme: {
      primary: 'bg-emerald-700',
      secondary: 'bg-emerald-600',
      accent: 'bg-yellow-400',
      headerBg: 'bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-700',
      footerBg: 'bg-emerald-700',
    },
    defaultSections: [
      { id: 'specials', title: 'WEEKLY SPECIALS', chineseTitle: '每週特價', headerColor: 'bg-emerald-600', bgColor: 'bg-gray-50', rows: 4, cols: 8, order: 0, visible: true },
      { id: 'clearance', title: 'CLEARANCE', chineseTitle: '清倉', headerColor: 'bg-orange-600', bgColor: 'bg-orange-50', rows: 3, cols: 8, order: 1, visible: true },
      { id: 'everyday', title: 'EVERYDAY LOW PRICES', chineseTitle: '天天低價', headerColor: 'bg-blue-600', bgColor: 'bg-blue-50', rows: 3, cols: 8, order: 2, visible: true },
    ],
  },
];

// ============ SECTION PRESETS ============

export const SECTION_PRESETS: Omit<SectionConfig, 'id' | 'order'>[] = [
  { title: 'PRODUCE', chineseTitle: '新鮮蔬果', headerColor: 'bg-green-600', bgColor: 'bg-green-50', rows: 2, cols: 6, visible: true },
  { title: 'MEAT', chineseTitle: '肉類', headerColor: 'bg-red-600', bgColor: 'bg-red-50', rows: 2, cols: 6, visible: true },
  { title: 'SEAFOOD', chineseTitle: '海鮮', headerColor: 'bg-blue-600', bgColor: 'bg-blue-50', rows: 2, cols: 6, visible: true },
  { title: 'GROCERY', chineseTitle: '雜貨', headerColor: 'bg-amber-500', bgColor: 'bg-amber-50', rows: 2, cols: 6, visible: true },
  { title: 'FROZEN', chineseTitle: '冷凍', headerColor: 'bg-cyan-600', bgColor: 'bg-cyan-50', rows: 2, cols: 6, visible: true },
  { title: 'DAIRY', chineseTitle: '乳製品', headerColor: 'bg-yellow-500', bgColor: 'bg-yellow-50', rows: 2, cols: 6, visible: true },
  { title: 'BAKERY', chineseTitle: '烘焙', headerColor: 'bg-orange-500', bgColor: 'bg-orange-50', rows: 2, cols: 6, visible: true },
  { title: 'BEVERAGES', chineseTitle: '飲料', headerColor: 'bg-purple-600', bgColor: 'bg-purple-50', rows: 2, cols: 6, visible: true },
  { title: 'SNACKS', chineseTitle: '零食', headerColor: 'bg-pink-500', bgColor: 'bg-pink-50', rows: 2, cols: 6, visible: true },
  { title: 'HALAL', chineseTitle: '清真', headerColor: 'bg-teal-600', bgColor: 'bg-teal-50', rows: 2, cols: 6, visible: true },
  { title: 'RESTAURANT SPECIAL', chineseTitle: '餐廳特價', headerColor: 'bg-red-700', bgColor: 'bg-red-50', rows: 2, cols: 8, visible: true },
  { title: 'STAR DEALS', chineseTitle: '明星優惠', headerColor: 'bg-yellow-500', bgColor: 'bg-yellow-50', rows: 2, cols: 5, visible: true },
];

// ============ HEADER COLOR OPTIONS ============

export const HEADER_COLORS = [
  { name: 'Green', value: 'bg-green-600' },
  { name: 'Red', value: 'bg-red-600' },
  { name: 'Blue', value: 'bg-blue-600' },
  { name: 'Amber', value: 'bg-amber-500' },
  { name: 'Orange', value: 'bg-orange-500' },
  { name: 'Purple', value: 'bg-purple-600' },
  { name: 'Pink', value: 'bg-pink-500' },
  { name: 'Cyan', value: 'bg-cyan-600' },
  { name: 'Teal', value: 'bg-teal-600' },
  { name: 'Yellow', value: 'bg-yellow-500' },
  { name: 'Emerald', value: 'bg-emerald-600' },
  { name: 'Gray', value: 'bg-gray-600' },
];

// ============ UTILITY FUNCTIONS ============

export function getThemeById(id: string): FlyerTheme | undefined {
  return THEMES.find(t => t.id === id);
}

export function generateSectionId(): string {
  return `section-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function cloneSections(sections: SectionConfig[]): SectionConfig[] {
  return sections.map(s => ({ ...s }));
}
